import { useTranslation } from "react-i18next";
import { EntityList } from "@/components/common/EntityList";
import { List } from "@/types/list";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, Star } from "lucide-react";
import { Link } from "react-router-dom";

interface ListsListProps {
  lists: List[];
  filteredLists: List[];
  isLoading: boolean;
  favorites?: Set<string>;
}

export const ListsList = ({ lists, filteredLists, isLoading, favorites = new Set() }: ListsListProps) => {
  const { t } = useTranslation();

  const columns = [
    {
      key: "name",
      header: t("lists.list.name", "Name"),
      cell: (list: List) => (
        <div>
          <div className="font-medium text-gray-900 flex items-center gap-2">
            {list.name}
            {favorites.has(list.id) && <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />}
          </div>
          {list.description && (
            <div className="text-sm text-gray-500 truncate max-w-xs">
              {list.description}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "candidateCount",
      header: t("lists.list.candidateCount", "Candidates"),
      cell: (list: List) => (
        <Badge variant="secondary">
          {list.candidate_count || 0}
        </Badge>
      ),
    },
    {
      key: "createdAt",
      header: t("lists.list.createdAt", "Created"),
      cell: (list: List) => (
        <div className="text-sm text-gray-500">
          {format(new Date(list.created_at), "MMM dd, yyyy")}
        </div>
      ),
    },
    {
      key: "actions",
      header: t("lists.list.actions", "Actions"),
      cell: (list: List) => (
        <Button asChild variant="outline" size="sm">
          <Link to={`/lists/${list.id}`}>
            <Eye className="h-4 w-4 mr-2" />
            {t("lists.list.view", "View")}
          </Link>
        </Button>
      ),
    },
  ];

  return (
    <EntityList
      data={filteredLists}
      columns={columns}
      isLoading={isLoading}
      emptyMessage={t("lists.empty", "No lists found")}
      basePath="/lists"
    />
  );
};
